<script setup lang="ts">
/**
 * A primary button that only confirms after a 600ms press and hold, with a ring that fills while it's held.
 * Approvals that send something out of Lighthouse for good (an email, an invoice, an invite to other people)
 * use it, so a stray tap in a scrolling chat can never approve them. Works with a finger, a mouse, Enter or
 * Space; a quick tap nudges the button and says "Keep holding" instead of doing anything.
 *
 * Assistive tech (TalkBack, Switch Access, Voice Control) activates with one synthetic click and no held
 * press, or with a long-press action that arrives as `contextmenu`. Such an activation arms the button ("Tap to
 * confirm", announced) and a second one within a few seconds confirms: still a second, deliberate
 * choice, but no timing. (Not a confirm sheet: on the voice call screen it would open underneath.)
 */
import { computed, onBeforeUnmount, ref } from 'vue'
import { usePressAndHold } from '@/composables/usePressAndHold'
import { haptic } from '@/native/haptics'
import { vPress } from '@/directives/press'

const HOLD_MS = 600
/** A click or contextmenu this long after the last pointerdown on the button didn't come from a finger or mouse. */
const POINTER_RECENT_MS = 1500
/** How long an assistive activation stays armed for the confirming second one. */
const ARMED_MS = 5000

const props = withDefaults(defineProps<{ label?: string; busy?: boolean; disabled?: boolean }>(), {
    label: 'Hold to approve',
    busy: false,
    disabled: false,
})
const emit = defineEmits<{ confirm: [] }>()

const inactive = () => props.disabled || props.busy
const hold = usePressAndHold(() => done(), { delay: HOLD_MS, haptic: 'commit', contextMenu: false, tolerance: 14, disabled: inactive })
const keyHolding = ref(false)
const holding = computed(() => hold.pressing.value || keyHolding.value)
const hint = ref(false)
let keyTimer: ReturnType<typeof setTimeout> | null = null
let hintTimer: ReturnType<typeof setTimeout> | null = null
let lastPointerDown = -Infinity
/** An assistive activation armed the button: the next one confirms. */
const armed = ref(false)
let armTimer: ReturnType<typeof setTimeout> | null = null

function disarm() {
    armed.value = false
    if (armTimer) clearTimeout(armTimer)
    armTimer = null
}

function done() {
    hint.value = false
    disarm()
    emit('confirm')
}

const handlers = {
    ...hold.handlers,
    onPointerdown(e: PointerEvent) {
        lastPointerDown = Date.now()
        hold.handlers.onPointerdown(e)
    },
}
const fromPointer = () => hold.pressing.value || Date.now() - lastPointerDown < POINTER_RECENT_MS

/** An activation with no held press behind it (assistive tech): the first arms, a second confirms. */
function assistiveActivate() {
    if (inactive()) return
    if (armed.value) {
        haptic('commit')
        done()
        return
    }
    hint.value = false
    armed.value = true
    if (armTimer) clearTimeout(armTimer)
    armTimer = setTimeout(disarm, ARMED_MS)
}

/** A click that got through was a tap (a completed hold swallows its click), or an assistive activation. */
function onClick() {
    if (inactive()) return
    if (!fromPointer()) {
        assistiveActivate()
        return
    }
    disarm()
    hint.value = false
    // Restart the nudge animation.
    requestAnimationFrame(() => { hint.value = true })
    if (hintTimer) clearTimeout(hintTimer)
    hintTimer = setTimeout(() => { hint.value = false }, 1800)
}

/** Android's assistive long press (TalkBack, Switch Access, Voice Access) arrives as a contextmenu with no press. */
function onContextmenu() {
    if (!fromPointer()) assistiveActivate()
}

function onKeydown(e: KeyboardEvent) {
    if (e.key !== 'Enter' && e.key !== ' ') return
    e.preventDefault()
    if (e.repeat || inactive() || keyHolding.value) return
    keyHolding.value = true
    keyTimer = setTimeout(() => {
        keyTimer = null
        keyHolding.value = false
        haptic('commit')
        done()
    }, HOLD_MS)
}
function onKeyup(e: KeyboardEvent) {
    if (e.key !== 'Enter' && e.key !== ' ') return
    e.preventDefault()
    if (keyTimer) clearTimeout(keyTimer)
    keyTimer = null
    keyHolding.value = false
}

onBeforeUnmount(() => {
    if (keyTimer) clearTimeout(keyTimer)
    if (hintTimer) clearTimeout(hintTimer)
    if (armTimer) clearTimeout(armTimer)
})
</script>

<template>
    <button
        v-press="{ variant: 'pill', disabled: inactive() }"
        type="button"
        class="hold"
        :class="{ 'is-holding': holding, 'is-hint': hint, 'is-armed': armed, 'is-busy': busy }"
        :disabled="disabled"
        :aria-busy="busy || undefined"
        :aria-label="armed ? 'Approve: activate again to confirm' : `${label}: press and hold`"
        data-testid="hold-approve"
        v-bind="handlers"
        @click="onClick"
        @keydown="onKeydown"
        @keyup="onKeyup"
        @contextmenu.prevent="onContextmenu"
    >
        <span class="hold-fill" aria-hidden="true" />
        <span class="hold-ring" aria-hidden="true">
            <span v-if="busy" class="hold-spin" />
            <svg v-else viewBox="0 0 20 20">
                <circle class="track" cx="10" cy="10" r="8" />
                <circle class="arc" cx="10" cy="10" r="8" />
                <path class="check" d="M6.6 10.3l2.2 2.2 4.6-4.9" />
            </svg>
        </span>
        <span class="hold-label">
            <Transition name="hold-swap" mode="out-in">
                <span :key="armed ? 'armed' : hint ? 'hint' : 'label'">{{ armed ? 'Tap to confirm' : hint ? 'Keep holding…' : label }}</span>
            </Transition>
        </span>
        <!-- Outside the button (its children are presentational to screen readers), so the hint is announced. -->
        <Teleport to="body">
            <span class="sr-only" aria-live="polite">{{ armed ? 'Activate again to approve' : hint ? 'Keep holding to approve' : '' }}</span>
        </Teleport>
    </button>
</template>

<style scoped>
.hold {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 44px;
    padding: 0 16px;
    overflow: hidden;
    border-radius: 14px;
    font-size: 15px;
    font-weight: 600;
    color: #fff;
    background: linear-gradient(180deg, var(--agent-accent-2, #ff7a4d) 0%, var(--agent-accent, #fb4c1f) 100%);
    box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.35), 0 6px 16px -6px rgb(251 76 31 / 0.6);
    touch-action: manipulation;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
    cursor: pointer;
}
.hold:disabled { opacity: 0.5; cursor: default; }
.hold-fill {
    position: absolute;
    inset: 0;
    background: rgb(255 255 255 / 0.22);
    transform: scaleX(0);
    transform-origin: left center;
    transition: transform 220ms var(--lh-ease-out, ease-out);
    pointer-events: none;
}
.hold.is-holding .hold-fill {
    transform: scaleX(1);
    transition: transform 600ms linear;
}
.hold-ring,
.hold-label { position: relative; display: inline-flex; align-items: center; }
.hold-ring svg { width: 20px; height: 20px; transform: rotate(-90deg); }
.hold-ring circle { fill: none; stroke-width: 2.2; }
.hold-ring .track { stroke: rgb(255 255 255 / 0.35); }
.hold-ring .arc {
    stroke: #fff;
    stroke-linecap: round;
    stroke-dasharray: 50.3;
    stroke-dashoffset: 50.3;
    transition: stroke-dashoffset 200ms var(--lh-ease-out, ease-out);
}
.hold.is-holding .arc {
    stroke-dashoffset: 0;
    transition: stroke-dashoffset 600ms linear;
}
.hold-ring .check {
    fill: none;
    stroke: #fff;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
    transform: rotate(90deg);
    transform-origin: 10px 10px;
    opacity: 0.9;
}
.hold-spin {
    width: 16px;
    height: 16px;
    border-radius: 999px;
    border: 2px solid rgb(255 255 255 / 0.4);
    border-top-color: #fff;
    animation: hold-spin 0.7s linear infinite;
}
.hold.is-hint { animation: hold-nudge 420ms var(--lh-spring-soft, ease-out); }
@keyframes hold-nudge {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-4px); }
    60% { transform: translateX(3px); }
}
@keyframes hold-spin { to { transform: rotate(360deg); } }
.hold-swap-enter-active, .hold-swap-leave-active { transition: opacity 140ms ease, transform 180ms var(--lh-ease-out, ease-out); }
.hold-swap-enter-from { opacity: 0; transform: translateY(5px); }
.hold-swap-leave-to { opacity: 0; transform: translateY(-5px); }
@media (prefers-reduced-motion: reduce) {
    .hold.is-hint { animation: none; }
    .hold.is-holding .hold-fill { transition: transform 600ms linear; }
}
</style>
